import {LogOut} from 'src/ui-components/Iconpack';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {NavLink} from 'src/ui-components/NavigationMenu/NavLink';
import {NestedNavLink} from 'src/ui-components/NavigationMenu/NestedNavLink';
import {NavLinkCaption} from 'src/ui-components/NavigationMenu/NavCaption';
import UserPlan from '../user-plan/UserPlan';
import {Avatar, AvatarImage} from 'src/ui-components/Avatar/avatar';
import PixelLogo from '../../assets/icons/pixel-logo.svg';

import User from '../../assets/icons/user-01.svg';
import {Button} from 'src/ui-components/Button/button';
import {NavigationItems, NavItemLinkProps} from './SidebarList';
import {RootState, useAppDispatch} from 'src/store';
import {useSelector} from 'react-redux';
import {logout} from 'src/actions/auth';
import Tracker from 'src/lib/analytics/tracker';
import {EventNames} from 'src/lib/analytics/events';
import PerceptLogoSmall from 'src/assets/icons/percept-logo-small.svg';
import Close from 'src/assets/icons/x-close.svg';
import BuildingIcon from 'src/assets/icons/building-05.svg';
import SwitchHorizontalIcon from 'src/assets/icons/switch-horizontal-01.svg';
import {
    Tooltip,
    TooltipArrow,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from 'src/ui-components/Tooltip/tooltip';
import {Dialog, DialogContent} from '../../ui-components/Modal';
import {OrganizationInterface, setCurrentOrganization} from '../../reducers/users';
import CoinIcon from '../../assets/icons/coin_icon.svg';
import {nFetch} from '../../api/nFetch';
import {getWalletBalance} from '../../actions/metrics';

interface HeaderProps {
    onCloseClick: () => void;
}

export const Sidebar = ({onCloseClick}: HeaderProps) => {
    const [userProfileProps, metricsProps] = [
        useSelector((state: RootState) => state.userProfile),
        useSelector((state: RootState) => state.metrics),
    ];

    const {first_name, last_name, email, referral_code, referral_count, currentOrganization} = userProfileProps;

    const [orgSelectorOpen, setOrgSelectorOpen] = useState(false);

    const location = useLocation();
    const [currentPath, setCurrentPath] = useState('');
    const [walletBalance, setWalletBalance] = useState(0);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const onClickLogout = () => {
        dispatch(logout()).then(() => {
            navigate('/');
        });
    };

    const pathMatch = (currentPath: string, itemPath: string): boolean => {
        return (
            currentPath === itemPath || (currentPath !== '/' && itemPath !== '/' && currentPath.startsWith(itemPath))
        );
    };

    const orgList: OrganizationInterface[] = [
        ...userProfileProps.my_organizations,
        ...userProfileProps.org_admins,
        ...userProfileProps.org_viewers,
    ];
    const orgMap = new Map<string, OrganizationInterface>();
    orgList.forEach((org) => {
        orgMap.set(org.uid, org);
    });
    const myOrg = userProfileProps.currentOrganization;
    const switchOrg = (org: OrganizationInterface) => {
        dispatch(setCurrentOrganization(org));
    };

    function menuAvailableForDevice(item: NavItemLinkProps): boolean {
        return window.innerWidth < 768 ? item.mobileAvailable : item.desktopAvailable;
    }

    function featureAvailable(menu: NavItemLinkProps): boolean {
        return !(menu.featureFlag && !currentOrganization.enabled_features.includes(menu.featureFlag));
    }

    useEffect(() => {
        setCurrentPath(location.pathname + location.search);
    }, [location.pathname, location.search]);

    useEffect(() => {
        if (myOrg.uid) {
            dispatch(getWalletBalance({org_uid: myOrg.uid}));
        }
    }, [myOrg.uid]);

    useEffect(() => {
        if (metricsProps.balance) {
            setWalletBalance(metricsProps.balance);
        }
    }, [metricsProps.balance]);

    return (
        <div className='flex max-h-screen w-full max-w-full shrink-0 flex-col justify-between overflow-y-auto bg-[#293056] px-5 md:w-80'>
            {/* top image */}
            <div className={'flex-none cursor-pointer py-5'}>
                <img
                    className='hidden md:flex md:w-4/5'
                    src={PixelLogo}
                    alt='PerceptPixel Logo'
                    onClick={() => {
                        Tracker.trackEvent(EventNames.PIXEL_ICON_CLICKED);
                        window.location.href = window.location.protocol + '//' + window.location.host;
                    }}
                />
                <div className='flex justify-between md:hidden'>
                    <img
                        src={PerceptLogoSmall}
                        alt='PerceptPixel Logo'
                        onClick={() => {
                            Tracker.trackEvent(EventNames.PIXEL_ICON_CLICKED);
                            window.location.href = window.location.protocol + '//' + window.location.host;
                        }}
                    />
                    <img className='cursor-pointer' src={Close} alt='close' onClick={onCloseClick} />
                </div>
            </div>
            {/* navbar */}
            <div className='flex-none pt-8'>
                <nav>
                    <ul className='flex flex-col gap-[4px]'>
                        {NavigationItems.map((item) => {
                            return menuAvailableForDevice(item) && featureAvailable(item) ? (
                                <li className='m-0 list-none' key={item.path}>
                                    {item.nestedItems ? (
                                        <NestedNavLink
                                            item={item}
                                            currentPath={currentPath}
                                            onCloseClick={onCloseClick}
                                            menuAvailableForDevice={menuAvailableForDevice}
                                            featureAvailable={featureAvailable}
                                        />
                                    ) : (
                                        <NavLink
                                            as={Link}
                                            asProps={{to: item.path, target: item.target}}
                                            active={pathMatch(currentPath, item.path)}
                                            onCloseClick={onCloseClick}>
                                            {item.icon && <item.icon />}
                                            <div className='flex'>
                                                <NavLinkCaption>{item.title} </NavLinkCaption>
                                                {item.title === 'Wallet' && (
                                                    <div className='flex gap-1'>
                                                        &nbsp;
                                                        <img src={CoinIcon} alt='coin' />
                                                        <span className='font-[600] text-[#F2F4F7]'>
                                                            {walletBalance}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </NavLink>
                                    )}
                                </li>
                            ) : null;
                        })}
                    </ul>
                </nav>
            </div>
            {/* spacer */}
            <div className={'grow'} />
            {/* user plan */}
            <div className={'hidden flex-none py-5 md:flex'}>
                <UserPlan referralCode={referral_code} referralCount={referral_count} tier={currentOrganization.tier} />
            </div>

            {/* divider */}
            {/* org selector */}
            {orgMap.size > 1 ? (
                <>
                    <hr className={'border-t border-[#475467]'} />
                    <div className='flex w-full max-w-full flex-row justify-between self-stretch py-2'>
                        <div className={'flex min-w-0 flex-row'}>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger content={'Switch Organization'}>
                                        <Avatar className={'mr-3'}>
                                            <div className='p-[0.5rem]'>
                                                <AvatarImage src={BuildingIcon} />
                                            </div>
                                        </Avatar>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        Organization
                                        <TooltipArrow />
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                            <div className='flex min-w-0 shrink flex-col text-[0.875rem] text-[#FFFFFF]'>
                                <span className='truncate font-[600]' title={`${myOrg.name}`}>
                                    {myOrg.name}
                                </span>
                                <span className='truncate font-[400]' title={myOrg.uid}>
                                    {myOrg.uid}
                                </span>
                            </div>
                        </div>
                        <div>
                            <Button
                                variant={'nude'}
                                onClick={() => {
                                    setOrgSelectorOpen(true);
                                }}>
                                <img className={'h-5 w-5'} src={SwitchHorizontalIcon} alt='Switch Organization' />
                            </Button>
                        </div>
                    </div>
                    <Dialog open={orgSelectorOpen}>
                        <DialogContent
                            className={'max-h-[85vh] w-[90vw] max-w-lg p-12'}
                            onClose={() => {
                                setOrgSelectorOpen(false);
                            }}>
                            <div className='flex flex-col gap-6'>
                                {Array.from(orgMap.entries()).map(([_, thisOrg]) => {
                                    return (
                                        <div key={thisOrg.uid} className='flex flex-row items-center'>
                                            <div className='mr-2 flex-none'>
                                                <Avatar>
                                                    <div className='p-[0.5rem]'>
                                                        <AvatarImage src={BuildingIcon} />
                                                    </div>
                                                </Avatar>
                                            </div>
                                            <div className='flex-grow'>
                                                <div className='flex flex-col gap-1'>
                                                    <span className='truncate font-[600]'>{thisOrg.name}</span>
                                                    <span className='truncate font-[400]'>{thisOrg.uid}</span>
                                                </div>
                                            </div>
                                            <div className={'flex-none'}>
                                                {thisOrg.uid === myOrg.uid ? (
                                                    <Button variant={'nude'} disabled>
                                                        <span>Current</span>
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant={'primary'}
                                                        onClick={() => {
                                                            switchOrg(thisOrg);
                                                        }}>
                                                        <span>Switch</span>
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </DialogContent>
                    </Dialog>
                </>
            ) : null}
            <hr className={'border-t border-[#475467]'} />
            {/* user profile */}
            <div className='flex w-full max-w-full flex-row justify-between self-stretch pb-24 pt-2 md:py-2'>
                <div className={'flex min-w-0 flex-row'}>
                    <Avatar className={'mr-3'}>
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger content={'User Profile'}>
                                    <div className='p-[0.5rem]'>
                                        <AvatarImage src={User} />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    User Profile
                                    <TooltipArrow />
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </Avatar>
                    <div className='flex min-w-0 shrink flex-col text-[0.875rem] text-[#FFFFFF]'>
                        <span className='truncate font-[600]' title={`${first_name} ${last_name}`}>
                            {first_name} {last_name}
                        </span>
                        <span className='truncate font-[400]' title={email}>
                            {email}
                        </span>
                    </div>
                </div>
                <div>
                    <Button
                        variant={'nude'}
                        onClick={() => {
                            onClickLogout();
                        }}>
                        <LogOut />
                    </Button>
                </div>
            </div>
        </div>
    );
};
